import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid
} from '@material-ui/core';
import { Search as SearchIcon, Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@material-ui/icons';
import { solicitudDetalleService } from '../services';
import { authenticationService } from '../services';

const Productores = () => {
  const [productores, setProductores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [busqueda, setBusqueda] = useState('');
  
  // Estados para el modal de crear/editar
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProductor, setEditingProductor] = useState(null);
  const [formData, setFormData] = useState({
    nombres: '',
    apellidos: '',
    correoElectronico: '',
    celular: ''
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const cargarProductores = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await solicitudDetalleService.obtenerProductores();
      setProductores(data);
    } catch (err) {
      setError(err.message || 'Error al cargar los productores');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authenticationService.checkSessionValidity()) {
      return;
    }
    cargarProductores();
  }, [cargarProductores]);

  // Filtrar productores
  const productoresFiltrados = useMemo(() => {
    if (!busqueda.trim()) return productores;
    
    const busquedaLower = busqueda.toLowerCase().trim();
    return productores.filter(p => 
      p.nombres?.toLowerCase().includes(busquedaLower) ||
      p.apellidos?.toLowerCase().includes(busquedaLower) ||
      p.correoElectronico?.toLowerCase().includes(busquedaLower) ||
      p.celular?.includes(busqueda)
    );
  }, [productores, busqueda]);

  const handleOpenDialog = (productor = null) => {
    if (productor) {
      setEditingProductor(productor);
      setFormData({
        nombres: productor.nombres || '',
        apellidos: productor.apellidos || '',
        correoElectronico: productor.correoElectronico || '',
        celular: productor.celular || ''
      });
    } else {
      setEditingProductor(null);
      setFormData({
        nombres: '',
        apellidos: '',
        correoElectronico: '',
        celular: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProductor(null);
    setFormData({
      nombres: '',
      apellidos: '',
      correoElectronico: '',
      celular: ''
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess('');

    try {
      if (editingProductor) {
        await solicitudDetalleService.actualizarProductor(editingProductor.id, formData);
        setSuccess('Productor actualizado exitosamente');
      } else {
        await solicitudDetalleService.crearProductor(formData);
        setSuccess('Productor creado exitosamente');
      }
      handleCloseDialog();
      await cargarProductores();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Error al guardar el productor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Está seguro de que desea eliminar este productor?')) {
      return;
    }

    setDeleting(true);
    setError(null);
    try {
      await solicitudDetalleService.eliminarProductor(id);
      setSuccess('Productor eliminado exitosamente');
      await cargarProductores();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Error al eliminar el productor');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={3}>
        <Typography variant="h4" component="h1">
          Gestión de Productores
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Nuevo Productor
        </Button>
      </Box>

      {error && (
        <Box p={2} mb={2} style={{ backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2">{error}</Typography>
            <Button size="small" onClick={() => setError(null)} style={{ minWidth: 'auto', padding: '4px 8px' }}>
              ×
            </Button>
          </Box>
        </Box>
      )}

      {success && (
        <Box p={2} mb={2} style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '4px' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2">{success}</Typography>
            <Button size="small" onClick={() => setSuccess('')} style={{ minWidth: 'auto', padding: '4px 8px' }}>
              ×
            </Button>
          </Box>
        </Box>
      )}

      {/* Búsqueda */}
      <Paper style={{ padding: '1rem', marginBottom: '1.5rem' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Buscar por nombre, apellido, email o celular..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={8}>
            <Typography variant="body2" color="textSecondary">
              Mostrando {productoresFiltrados.length} de {productores.length} productores
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabla de productores */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Nombres</strong></TableCell>
              <TableCell><strong>Apellidos</strong></TableCell>
              <TableCell><strong>Correo Electrónico</strong></TableCell>
              <TableCell align="center"><strong>Acciones</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {productoresFiltrados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="textSecondary" style={{ padding: '2rem' }}>
                    {productores.length === 0 
                      ? 'No hay productores disponibles' 
                      : 'No se encontraron productores con los filtros aplicados'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              productoresFiltrados.map((productor) => (
                <TableRow key={productor.id} hover>
                  <TableCell>{productor.nombres || '-'}</TableCell>
                  <TableCell>{productor.apellidos || '-'}</TableCell>
                  <TableCell>{productor.correoElectronico || '-'}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(productor)}
                      disabled={deleting}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="secondary"
                      onClick={() => handleDelete(productor.id)}
                      disabled={deleting}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para crear/editar */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingProductor ? 'Editar Productor' : 'Nuevo Productor'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} style={{ marginTop: '0.5rem' }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nombres"
                  variant="outlined"
                  required
                  value={formData.nombres}
                  onChange={(e) => handleInputChange('nombres', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Apellidos"
                  variant="outlined"
                  required
                  value={formData.apellidos}
                  onChange={(e) => handleInputChange('apellidos', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Correo Electrónico"
                  variant="outlined"
                  type="email"
                  required
                  value={formData.correoElectronico}
                  onChange={(e) => handleInputChange('correoElectronico', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Celular"
                  variant="outlined"
                  value={formData.celular}
                  onChange={(e) => handleInputChange('celular', e.target.value)}
                  placeholder="+5491123456789"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" color="primary" disabled={saving}>
              {saving ? <CircularProgress size={20} /> : (editingProductor ? 'Actualizar' : 'Crear')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Productores;
